import React, { createContext, useContext, useState, useCallback } from 'react';

interface ChatUser {
  id: string;
  prenom: string;
  nom: string;
  photoProfil?: string | null;
}

interface ChatUIContextType {
  activeUser: ChatUser | null;
  isOpen: boolean;
  isMinimized: boolean;
  openChat: (user: ChatUser) => void;
  closeChat: () => void;
  setMinimized: (min: boolean) => void;
}

const ChatUIContext = createContext<ChatUIContextType>({
  activeUser: null,
  isOpen: false,
  isMinimized: false,
  openChat: () => {},
  closeChat: () => {},
  setMinimized: () => {},
});

export const ChatUIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeUser, setActiveUser] = useState<ChatUser | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const openChat = useCallback((user: ChatUser) => {
    setActiveUser(user);
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setActiveUser(null);
  }, []);

  const setMinimized = useCallback((min: boolean) => {
    setIsMinimized(min);
  }, []);

  const value = React.useMemo(() => ({ 
    activeUser, 
    isOpen, 
    isMinimized, 
    openChat, 
    closeChat, 
    setMinimized 
  }), [activeUser, isOpen, isMinimized, openChat, closeChat, setMinimized]);

  return (
    <ChatUIContext.Provider value={value}>
      {children}
    </ChatUIContext.Provider>
  );
};

export const useChatUI = () => useContext(ChatUIContext);
